import { cloneDeep } from 'lodash';
import moment from 'moment';

import { slugifyName } from 'bundles/common/utils/commonUtils';
import type {
  CourseMaterialsData,
  CourseMaterialsDataModuleLesson,
  CourseMaterialsDataModuleLessonItem,
} from 'bundles/in-course-stores/constants/CourseType';

import { ADAPTIVE_AI_GENERATED_ITEM_TYPE } from './constants';

export type AdaptiveLearningAiGeneratedMaterialItem = {
  id: string;
  name: string;
};

const SYNTHETIC_LESSON_DISPLAY_NAME = 'AI generated';
const SYNTHETIC_LESSON_ID = '__coursera_adaptive_ai_synthetic_lesson__';

const createFakeAiGeneratedItem = (id: string, displayName: string): CourseMaterialsDataModuleLessonItem => ({
  name: displayName,
  slug: slugifyName(displayName),
  id,
  trackId: 'core',
  timeCommitment: moment.duration(0).asMilliseconds(),
  contentSummary: {
    typeName: ADAPTIVE_AI_GENERATED_ITEM_TYPE,
    definition: {},
  } as unknown as CourseMaterialsDataModuleLessonItem['contentSummary'],
});

const createFakeAiGeneratedLesson = (
  items: AdaptiveLearningAiGeneratedMaterialItem[]
): CourseMaterialsDataModuleLesson => {
  const lessonItems = items.map((row) => createFakeAiGeneratedItem(row.id, row.name));
  return {
    id: SYNTHETIC_LESSON_ID,
    name: SYNTHETIC_LESSON_DISPLAY_NAME,
    slug: slugifyName(`${SYNTHETIC_LESSON_DISPLAY_NAME}-lesson`),
    trackId: 'core',
    timeCommitment: 0,
    itemGroups: [],
    elements: lessonItems,
  };
};

/**
 * Injects AI-generated adaptive learning items into a fake lesson
 * appended at the end of the course materials data.
 */
export const injectAdaptiveLearningAiGeneratedItems = (
  courseMaterialsData: CourseMaterialsData | undefined,
  aiGeneratedItems: AdaptiveLearningAiGeneratedMaterialItem[] = []
): CourseMaterialsData | undefined => {
  if (!courseMaterialsData?.elements?.length) {
    return courseMaterialsData;
  }

  if (aiGeneratedItems.length === 0) {
    return courseMaterialsData;
  }

  const result = cloneDeep(courseMaterialsData);
  const lastModule = result.elements[result.elements.length - 1];
  if (!lastModule) {
    return result;
  }

  const fakeAiGeneratedLesson = createFakeAiGeneratedLesson(aiGeneratedItems);
  lastModule.elements.push(fakeAiGeneratedLesson);

  return result;
};
